import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ragApi, RagApiError } from '@/services/ApiService'
import type { ApiDocument } from '@/lib/types'
import { DataTransformers } from '@/lib/types'
import type { Document, DocumentUploadData, URL, URLUploadData } from '@/lib/types'
import { queryKeys } from '../keys'

/**
 * Hook for uploading a document
 * Includes optimistic updates for better UX
 */
export function useUploadDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: DocumentUploadData) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('document_type', data.document_type)
      if (data.description) formData.append('description', data.description)
      if (data.category) formData.append('category', data.category)
      if (data.subcategory) formData.append('subcategory', data.subcategory)
      if (data.file) formData.append('file', data.file)
      formData.append('status', 'pending')

      const apiResponse = await ragApi.createDocument(formData) as ApiDocument
      return DataTransformers.apiDocumentToDocument(apiResponse) as Document
    },
    onSuccess: () => {
      // Invalidate documents list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
    },
    // **`onError` no relanza**, y esto no es estilo. Lo que se lance aqui no lo
    // recibe quien llamo: `mutateAsync` ya rechazo con el error de verdad, asi
    // que este segundo `throw` no lo puede recoger ningun `catch` y sale como
    // rechazo sin atender. Se vio el 2026-09-22 al usar esta mutacion por
    // primera vez -no la llamaba nadie-: las pruebas pasaban y Vitest reportaba
    // «Unhandled Rejection» al lado.
  })
}

/**
 * Hook for deleting a document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (documentId: string) => {
      await ragApi.deleteDocument(documentId)
      return documentId
    },
    onMutate: async (documentId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.documents.all })

      // Optimistically remove from cache
      queryClient.setQueriesData<Document[]>(
        { queryKey: queryKeys.documents.lists() },
        (old) => old?.filter(doc => doc.id !== documentId) || []
      )

      return { previousDocuments: queryClient.getQueriesData({ queryKey: queryKeys.documents.lists() }) }
    },
    onError: (err, _documentId, context) => {
      // Rollback on error
      if (context?.previousDocuments) {
        context.previousDocuments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      const errorMessage = err instanceof RagApiError
        ? err.message
        : 'Failed to delete document'
      throw new Error(errorMessage)
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
    },
  })
}

/**
 * Hook for updating a document
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Document> }) => {
      // Transform frontend data to API format
      const apiData: Record<string, unknown> = {}
      if (data.title && data.title.trim() !== '') apiData.title = data.title
      if (data.description && data.description.trim() !== '') apiData.description = data.description
      if (data.category && data.category.trim() !== '') {
        const categoryId = parseInt(data.category)
        if (!isNaN(categoryId)) apiData.category = categoryId
      }
      if (data.subcategory && data.subcategory.trim() !== '') {
        const subcategoryId = parseInt(data.subcategory)
        if (!isNaN(subcategoryId)) apiData.subcategory = subcategoryId
      }

      if (Object.keys(apiData).length === 0) {
        throw new Error('No data to update')
      }

      await ragApi.updateDocument(id, apiData)
      
      // Refresh the document from server to get updated category/subcategory names
      const updatedDocument = await ragApi.getDocument(id) as ApiDocument
      return DataTransformers.apiDocumentToDocument(updatedDocument) as Document
    },
    onSuccess: (updatedDocument) => {
      // Update cache with new document
      queryClient.setQueriesData<Document[]>(
        { queryKey: queryKeys.documents.lists() },
        (old) => old?.map(doc => 
          doc.id === updatedDocument.id ? updatedDocument : doc
        ) || []
      )
      
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.lists() })
    },
    onError: (err) => {
      const errorMessage = err instanceof RagApiError
        ? err.message
        : 'Failed to update document'
      throw new Error(errorMessage)
    },
  })
}

/**
 * Hook for uploading a URL
 */
export function useUploadURL() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: URLUploadData) => {
      const apiData = DataTransformers.urlUploadToApiData(data) as Record<string, unknown>
      const apiResponse = await ragApi.processURL(apiData) as ApiDocument
      return DataTransformers.apiDocumentToURL(apiResponse) as URL
    },
    onSuccess: () => {
      // Invalidate URLs list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.urls.lists() })
    },
    onError: (err) => {
      const errorMessage = err instanceof RagApiError
        ? err.message
        : 'Failed to upload URL'
      throw new Error(errorMessage)
    },
  })
}

/**
 * Hook for deleting a URL
 */
export function useDeleteURL() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (urlId: string) => {
      await ragApi.deleteDocument(urlId)
      return urlId
    },
    onMutate: async (urlId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.urls.all })

      // Optimistically remove from cache
      queryClient.setQueriesData<URL[]>(
        { queryKey: queryKeys.urls.lists() },
        (old) => old?.filter(url => url.id !== urlId) || []
      )

      return { previousURLs: queryClient.getQueriesData({ queryKey: queryKeys.urls.lists() }) }
    },
    onError: (err, _urlId, context) => {
      // Rollback on error
      if (context?.previousURLs) {
        context.previousURLs.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      const errorMessage = err instanceof RagApiError
        ? err.message
        : 'Failed to delete URL'
      throw new Error(errorMessage)
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.urls.lists() })
    },
  })
}

/**
 * Hook for updating a URL
 */
export function useUpdateURL() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ 
      id, 
      data,
      categories,
    }: { 
      id: string
      data: Partial<URL>
      categories?: { id: string; name: string; subcategories: { id: string; name: string }[] }[]
    }) => {
      // Transform frontend data to API format
      const apiData: Record<string, unknown> = {}
      if (data.title && data.title.trim() !== '') apiData.title = data.title
      if (data.description && data.description.trim() !== '') apiData.description = data.description
      if (data.url && data.url.trim() !== '') apiData.url = data.url

      // Always include document_type for URLs
      apiData.document_type = 'url'

      // Handle category and subcategory
      const existingUrl = queryClient.getQueriesData<URL[]>({ queryKey: queryKeys.urls.lists() })
        .flatMap(([, urls]) => urls || [])
        .find(url => url.id === id)

      if (data.category && data.category.trim() !== '') {
        const categoryId = parseInt(data.category)
        if (!isNaN(categoryId)) apiData.category = categoryId
      } else if (existingUrl && categories) {
        const categoryObj = categories.find(cat => cat.name === existingUrl.category)
        if (categoryObj) {
          apiData.category = parseInt(categoryObj.id)
        }
      }

      if (data.subcategory && data.subcategory.trim() !== '') {
        const subcategoryId = parseInt(data.subcategory)
        if (!isNaN(subcategoryId)) apiData.subcategory = subcategoryId
      } else if (existingUrl && categories) {
        const categoryObj = categories.find(cat => cat.name === existingUrl.category)
        if (categoryObj) {
          const subcategoryObj = categoryObj.subcategories.find(sub => sub.name === existingUrl.subcategory)
          if (subcategoryObj) {
            apiData.subcategory = parseInt(subcategoryObj.id)
          }
        }
      }

      if (Object.keys(apiData).length === 0) {
        throw new Error('No data to update')
      }

      await ragApi.updateDocument(id, apiData)
      
      // Refresh the URL from server to get updated category/subcategory names
      const updatedDocument = await ragApi.getDocument(id) as ApiDocument
      return DataTransformers.apiDocumentToURL(updatedDocument) as URL
    },
    onSuccess: (updatedURL) => {
      // Update cache with new URL
      queryClient.setQueriesData<URL[]>(
        { queryKey: queryKeys.urls.lists() },
        (old) => old?.map(url => 
          url.id === updatedURL.id ? updatedURL : url
        ) || []
      )
      
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.urls.lists() })
    },
    onError: (err) => {
      const errorMessage = err instanceof RagApiError
        ? err.message
        : 'Failed to update URL'
      throw new Error(errorMessage)
    },
  })
}
